import { STALE_THRESHOLD_MINUTES } from '@/constants/tracking'
import type { LocationCircle } from '@/hooks/useActiveLocations'
import { haversineDistance } from '@/lib/map-utils'
import { isCurrentlyWithinWorkingHours, workingHoursRelation } from '@/lib/working-hours'
import type { PathPoint } from '@/types/movement'
import type { LiveStatus, UserTrackingStatus } from '@/types/trackingStatus'
import { DEFAULT_WORKING_HOURS, type ResolvedWorkingHours } from '@/types/workingHours'

export interface ClassifyUserInput {
    userId: string
    geoTrackingDisabled: boolean
    mostRecentLocation: PathPoint | undefined
    path: PathPoint[]
    hasAnyMovementToday: boolean
    // The job the user is currently clocked into (from their active
    // non-break time_entry). When set, on-site detection only considers
    // geofences belonging to this job — being physically inside another
    // job's site doesn't count. When undefined the user has no active
    // entry, which during working hours surfaces as `working_untracked`.
    activeJobId?: string
    workingHours: ResolvedWorkingHours
    geofences: LocationCircle[]
    now: Date
}

function isInsideCircle(point: PathPoint, circle: LocationCircle): boolean {
    const distance = haversineDistance(
        point.latitude,
        point.longitude,
        circle.latitude,
        circle.longitude
    )
    return distance <= circle.radius
}

function findContainingJobGeofence(
    point: PathPoint,
    geofences: LocationCircle[],
    activeJobId: string
): LocationCircle | null {
    for (const circle of geofences) {
        if (circle.jobId !== activeJobId) continue
        if (isInsideCircle(point, circle)) return circle
    }
    return null
}

function computeOnSiteSince(
    path: PathPoint[],
    mostRecent: PathPoint,
    site: LocationCircle
): string {
    let earliest = mostRecent.timestamp
    for (let i = path.length - 1; i >= 0; i--) {
        const point = path[i]
        if (point.timestamp === mostRecent.timestamp) continue
        if (!isInsideCircle(point, site)) break
        earliest = point.timestamp
    }
    return earliest
}

interface LastSiteVisit {
    site: LocationCircle
    leftAt: string
}

function findLastSiteVisitIn(path: PathPoint[], circles: LocationCircle[]): LastSiteVisit | null {
    if (circles.length === 0) return null
    const sortedByTime = [...path].sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    for (let i = sortedByTime.length - 1; i >= 0; i--) {
        const point = sortedByTime[i]
        for (const circle of circles) {
            if (isInsideCircle(point, circle)) {
                return { site: circle, leftAt: point.timestamp }
            }
        }
    }
    return null
}

function findLastJobSiteVisit(
    path: PathPoint[],
    geofences: LocationCircle[],
    activeJobId: string
): LastSiteVisit | null {
    return findLastSiteVisitIn(
        path,
        geofences.filter(c => c.jobId === activeJobId)
    )
}

interface SharedFields {
    userId: string
    lastUpdate: string | null
    workingHours: ResolvedWorkingHours
    minutesSinceLastUpdate: number | null
}

function classifyOutsideHours(
    relation: ReturnType<typeof workingHoursRelation>,
    isClockedIn: boolean,
    shared: SharedFields
): UserTrackingStatus {
    if (isClockedIn) {
        const lateStatus: LiveStatus =
            relation === 'before' ? 'before_hours_working' : 'after_hours_working'
        return {
            ...shared,
            status: lateStatus,
            isWithinWorkingHours: false,
            geoTrackingDisabled: false,
        }
    }
    return {
        ...shared,
        status: 'off_hours',
        isWithinWorkingHours: false,
        geoTrackingDisabled: false,
    }
}

function classifyWithinHours(
    activeJobId: string,
    mostRecentLocation: PathPoint | undefined,
    path: PathPoint[],
    geofences: LocationCircle[],
    now: Date,
    shared: SharedFields
): UserTrackingStatus {
    const { minutesSinceLastUpdate } = shared
    const baseFields = {
        ...shared,
        isWithinWorkingHours: true,
        geoTrackingDisabled: false,
    } as const

    const isFreshlyMoving =
        mostRecentLocation &&
        minutesSinceLastUpdate !== null &&
        minutesSinceLastUpdate <= STALE_THRESHOLD_MINUTES &&
        mostRecentLocation.is_moving

    if (isFreshlyMoving) {
        const lastVisit = findLastJobSiteVisit(path, geofences, activeJobId)
        return {
            ...baseFields,
            status: 'working_moving',
            ...(lastVisit && {
                lastSiteJobId: lastVisit.site.id,
                lastSiteJobName: lastVisit.site.jobName,
                lastSiteLocationName: lastVisit.site.name,
                leftSiteAt: lastVisit.leftAt,
            }),
        }
    }

    const site = mostRecentLocation
        ? findContainingJobGeofence(mostRecentLocation, geofences, activeJobId)
        : null
    if (site && mostRecentLocation) {
        const onSiteSinceTimestamp = computeOnSiteSince(path, mostRecentLocation, site)
        const stationaryDurationMinutes = Math.max(
            0,
            Math.floor((now.getTime() - new Date(onSiteSinceTimestamp).getTime()) / 60_000)
        )
        return {
            ...baseFields,
            status: 'working_on_site',
            siteJobId: site.id,
            siteJobName: site.jobName,
            siteLocationName: site.name,
            stationaryDurationMinutes,
            onSiteSinceTimestamp,
        }
    }

    const lastVisit = findLastJobSiteVisit(path, geofences, activeJobId)
    return {
        ...baseFields,
        status: 'working_idle',
        ...(lastVisit && {
            lastSiteJobId: lastVisit.site.id,
            lastSiteJobName: lastVisit.site.jobName,
            lastSiteLocationName: lastVisit.site.name,
            leftSiteAt: lastVisit.leftAt,
        }),
    }
}

export function classifyUserActivity(input: ClassifyUserInput): UserTrackingStatus {
    const {
        userId,
        geoTrackingDisabled,
        mostRecentLocation,
        path,
        hasAnyMovementToday,
        activeJobId,
        workingHours,
        geofences,
        now,
    } = input

    const isClockedIn = activeJobId != null

    const lastUpdate = mostRecentLocation?.timestamp ?? null
    const minutesSinceLastUpdate = lastUpdate
        ? Math.floor((now.getTime() - new Date(lastUpdate).getTime()) / 60_000)
        : null

    const shared: SharedFields = { userId, lastUpdate, workingHours, minutesSinceLastUpdate }

    if (geoTrackingDisabled) {
        return {
            ...shared,
            workingHours: { ...DEFAULT_WORKING_HOURS, source: 'default' },
            status: 'not_tracking',
            isWithinWorkingHours: false,
            geoTrackingDisabled: true,
        }
    }

    if (!hasAnyMovementToday && !mostRecentLocation && !isClockedIn) {
        return {
            ...shared,
            status: 'no_data_today',
            isWithinWorkingHours: isCurrentlyWithinWorkingHours(workingHours),
            geoTrackingDisabled: false,
        }
    }

    const relation = workingHoursRelation(workingHours, now)
    if (relation !== 'within') {
        return classifyOutsideHours(relation, isClockedIn, shared)
    }

    // Within working hours but not on the clock for any job. The user is
    // pinging GPS during the work window without a timer running — managers
    // need to see this surface immediately so they can prompt the worker
    // to clock in. Their actual GPS is still shown so dispatch can find
    // them on the map.
    if (!activeJobId) {
        if (!mostRecentLocation) {
            return {
                ...shared,
                status: 'no_data_today',
                isWithinWorkingHours: true,
                geoTrackingDisabled: false,
            }
        }
        const lastVisit = findLastSiteVisitIn(path, geofences)
        return {
            ...shared,
            status: 'working_untracked',
            isWithinWorkingHours: true,
            geoTrackingDisabled: false,
            ...(lastVisit && {
                lastSiteJobId: lastVisit.site.id,
                lastSiteJobName: lastVisit.site.jobName,
                lastSiteLocationName: lastVisit.site.name,
                leftSiteAt: lastVisit.leftAt,
            }),
        }
    }

    return classifyWithinHours(activeJobId, mostRecentLocation, path, geofences, now, shared)
}

export type { LiveStatus }
